import React, { useState, useEffect } from 'react';
import {
  Page,
  Card,
  Button,
  Text,
  Box,
  InlineStack,
  BlockStack,
  Badge,
  Banner,
  Spinner,
  Select,
  DataTable,
  Modal,
  ProgressBar,
  Divider,
  Pagination,
  DatePicker,
  Popover,
  TextField,
} from '@shopify/polaris';

const API_BASE = window.location.origin;

function toYMD(date) {
  if (!date) return '';
  const d = new Date(date);
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}

function locationTokens(str) {
  return (str || '')
    .toLowerCase()
    .replace(/soccer\s*post/gi, '')
    .replace(/\bsp\b/gi, '')
    .replace(/\b(corp|llc|inc|warehouse|virtual|store)\b/gi, '')
    .replace(/[^a-z0-9\s]/g, ' ')
    .split(/\s+/)
    .filter(w => w.length >= 2);
}

// Best shop by token overlap. Place-name tokens (3+ chars) decide the match;
// 2-letter state codes only break ties. A state code alone is NOT a match —
// "Tinton Falls Warehouse" must not fall through to 'NJ Eatontown' just
// because both say NJ.
function scoreShops(orderTokens, shops, tieTokens) {
  const scored = [];
  for (const shop of shops) {
    const overlap = locationTokens(shop.name).filter(t => orderTokens.includes(t));
    const strong = overlap.filter(t => t.length >= 3).length;
    if (strong === 0) continue;
    scored.push({ score: strong * 10 + (overlap.length - strong), shop });
  }
  if (!scored.length) return null;
  const top = Math.max(...scored.map(s => s.score));
  const tied = scored.filter(s => s.score === top).map(s => s.shop);
  // "Soccer Post Austin" ships to Austin South as well as Austin Central: the account's own location name breaks the tie
  if (tied.length > 1 && tieTokens && tieTokens.length) return scoreShops(tieTokens, tied) || tied[0];
  return tied[0];
}

// Two-pass shop match. Shipping fields are tried FIRST and win outright:
// regional managers can log in under one store (company location) while
// shipping to another — the ship-to store is where the PO belongs.
// Account fields are only a fallback when shipping matches nothing.
// (Mirrors guess_shop_from_order in b2b_po_nightly.py — keep in sync.)
function guessShopFromOrder(order, shops) {
  const shippingTokens = [order.shipping_name, order.shipping_city, order.destination]
    .filter(Boolean).flatMap(locationTokens);
  const accountTokens = [order.company, order.company_location_name, order.customer_name]
    .filter(Boolean).flatMap(locationTokens);
  return scoreShops(shippingTokens, shops, accountTokens) || scoreShops(accountTokens, shops);
}

function authHeaders() {
  return { Authorization: `Bearer ${localStorage.getItem('access_token')}` };
}

function fmtDate(d) {
  if (!d) return '';
  return new Date(d).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
}

function fmtDateShort(d) {
  if (!d) return '';
  return new Date(d).toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
}

function fmtPrice(p) {
  const n = parseFloat(p);
  return isNaN(n) ? p : `$${n.toFixed(2)}`;
}

function FulfillmentBadge({ status }) {
  const map = { fulfilled: 'success', partial: 'info', unfulfilled: 'attention', restocked: 'info', cancelled: 'critical' };
  const label = (status || 'unfulfilled').replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase());
  if (status === 'on_hold') {
    return (
      <span style={{
        display: 'inline-flex', alignItems: 'center', gap: 5,
        backgroundColor: '#fde8d1', color: '#7c3f00',
        borderRadius: 10, padding: '2px 8px', fontSize: 12, fontWeight: 500, whiteSpace: 'nowrap',
      }}>
        <span style={{ width: 8, height: 8, borderRadius: '50%', backgroundColor: '#e07c18', flexShrink: 0, display: 'inline-block' }} />
        On hold
      </span>
    );
  }
  return <Badge tone={map[status] || 'default'}>{label}</Badge>;
}

function SkuBadge({ found }) {
  return found ? <Badge tone="success">Found</Badge> : <Badge tone="critical">Not Found</Badge>;
}

export default function B2BPurchaseOrders() {
  // ── Orders list ───────────────────────────────────────────────────────────
  const [orders, setOrders]               = useState([]);
  const [ordersLoading, setOrdersLoading] = useState(false);
  const [ordersError, setOrdersError]     = useState('');

  // Server-side cursor pagination
  const [cursors, setCursors]         = useState([null]);
  const [pageIndex, setPageIndex]     = useState(0);
  const [hasNextPage, setHasNextPage] = useState(false);

  // ── Filters ───────────────────────────────────────────────────────────────
  const [companyLocations, setCompanyLocations]   = useState([]);
  const [locationsLoading, setLocationsLoading]   = useState(false);
  const [filterLocationId, setFilterLocationId]   = useState('');
  const [hidePOCreated, setHidePOCreated]         = useState(false);
  const [hidePOIgnored, setHidePOIgnored]           = useState(false);
  const [onlySkuMismatch, setOnlySkuMismatch]       = useState(false);
  const [selectedForIgnore, setSelectedForIgnore]   = useState(new Set());
  const [ignoring, setIgnoring]                     = useState(false);

  // Date range picker state
  const [datePopoverActive, setDatePopoverActive]   = useState(false);
  const [selectedDateRange, setSelectedDateRange]   = useState({ start: null, end: null });
  const [datePickerMonth, setDatePickerMonth]       = useState({
    month: new Date().getMonth(),
    year:  new Date().getFullYear(),
  });

  // Applied server-side filters (drive actual fetches)
  const [appliedDateFrom, setAppliedDateFrom]       = useState('');
  const [appliedDateTo, setAppliedDateTo]           = useState('');
  const [appliedLocationId, setAppliedLocationId]   = useState('');

  // ── Selected order + line resolution ─────────────────────────────────────
  const [selectedOrder, setSelectedOrder]   = useState(null);
  const [orderDetail, setOrderDetail]       = useState(null);
  const [lines, setLines]                   = useState([]);
  const [detailLoading, setDetailLoading]   = useState(false);
  const [detailError, setDetailError]       = useState('');
  const [skuProgress, setSkuProgress]       = useState(null);

  // ── Lightspeed shops ──────────────────────────────────────────────────────
  const [shops, setShops]                     = useState([]);
  const [shopsLoading, setShopsLoading]       = useState(false);
  const [selectedShop, setSelectedShop]       = useState('');
  const [autoMatchedShop, setAutoMatchedShop] = useState(null);
  const [autoMatchFailed, setAutoMatchFailed] = useState(false);

  // ── PO creation ───────────────────────────────────────────────────────────
  const [creating, setCreating]           = useState(false);
  const [createLog, setCreateLog]         = useState([]);
  const [createResult, setCreateResult]   = useState(null);
  const [createError, setCreateError]     = useState('');
  const [modalOpen, setModalOpen]         = useState(false);
  const [progress, setProgress]           = useState(0);
  const [progressTotal, setProgressTotal] = useState(0);
  const [poNote, setPoNote]               = useState('');

  // ── Auto-match Lightspeed shop ────────────────────────────────────────────
  useEffect(() => {
    if (!selectedOrder || shops.length === 0) return;
    const match = guessShopFromOrder(selectedOrder, shops);
    if (match) {
      setSelectedShop(match.shopID);
      setAutoMatchedShop(match);
      setAutoMatchFailed(false);
    } else {
      setAutoMatchedShop(null);
      setAutoMatchFailed(true);
    }
  }, [selectedOrder, shops]);

  // ── Load on mount ─────────────────────────────────────────────────────────
  useEffect(() => {
    fetchPage({ cursor: null, dateFrom: '', dateTo: '', locationId: '', targetIndex: 0 });
    loadShops();
    loadCompanyLocations();
  }, []);

  const fetchPage = async ({ cursor, dateFrom, dateTo, locationId, targetIndex, tag }) => {
    setOrdersLoading(true);
    setOrdersError('');
    try {
      const params = new URLSearchParams({ limit: 25 });
      if (cursor)     params.set('after', cursor);
      if (dateFrom)   params.set('date_from', dateFrom);
      if (dateTo)     params.set('date_to', dateTo);
      if (locationId) params.set('location_id', locationId);
      if (tag)        params.set('tag', tag);
      const r = await fetch(`${API_BASE}/api/b2b-po/orders?${params}`, { headers: authHeaders() });
      if (!r.ok) throw new Error(`Server error ${r.status}`);
      const data = await r.json();
      setOrders(data.orders || []);
      setSelectedForIgnore(new Set());
      setHasNextPage(data.hasNextPage || false);
      if (data.endCursor && data.orders?.length > 0) {
        const idx = targetIndex ?? 0;
        setCursors(prev => { const next = [...prev]; next[idx + 1] = data.endCursor; return next; });
      }
    } catch (e) {
      setOrdersError(e.message);
    } finally {
      setOrdersLoading(false);
    }
  };

  const loadCompanyLocations = async () => {
    setLocationsLoading(true);
    try {
      const r = await fetch(`${API_BASE}/api/b2b-po/company-locations`, { headers: authHeaders() });
      if (!r.ok) throw new Error(`Server error ${r.status}`);
      const data = await r.json();
      setCompanyLocations(data.locations || []);
    } catch (e) {
      console.error('Could not load company locations:', e);
    } finally {
      setLocationsLoading(false);
    }
  };

  const loadShops = async () => {
    setShopsLoading(true);
    try {
      const r = await fetch(`${API_BASE}/api/b2b-po/ls-shops`, { headers: authHeaders() });
      if (!r.ok) throw new Error(`Server error ${r.status}`);
      const data = await r.json();
      const shopList = data.shops || [];
      setShops(shopList);
      if (shopList.length > 0) setSelectedShop(shopList[0].shopID);
    } catch (e) {
      console.error('Could not load LS shops:', e);
    } finally {
      setShopsLoading(false);
    }
  };

  const handleSearch = () => {
    const from = toYMD(selectedDateRange.start);
    const to   = toYMD(selectedDateRange.end || selectedDateRange.start);
    setAppliedDateFrom(from);
    setAppliedDateTo(to);
    setAppliedLocationId(filterLocationId);
    setDatePopoverActive(false);
    setCursors([null]);
    setPageIndex(0);
    fetchPage({ cursor: null, dateFrom: from, dateTo: to, locationId: filterLocationId, targetIndex: 0, tag: onlySkuMismatch ? 'SKU Mismatch' : '' });
  };

  const handleReset = () => {
    setSelectedDateRange({ start: null, end: null });
    setAppliedDateFrom('');
    setAppliedDateTo('');
    setAppliedLocationId('');
    setFilterLocationId('');
    setCursors([null]);
    setPageIndex(0);
    fetchPage({ cursor: null, dateFrom: '', dateTo: '', locationId: '', targetIndex: 0 });
  };

  const handleIgnore = async () => {
    if (selectedForIgnore.size === 0) return;
    setIgnoring(true);
    const ids = [...selectedForIgnore];
    const results = await Promise.all(
      ids.map(async (orderId) => {
        const numericId = String(orderId).includes('/') ? parseInt(orderId.split('/').pop(), 10) : orderId;
        try {
          const r = await fetch(`${API_BASE}/api/b2b-po/tag-ignored`, {
            method: 'POST',
            headers: { ...authHeaders(), 'Content-Type': 'application/json' },
            body: JSON.stringify({ order_id: numericId }),
          });
          return r.ok ? orderId : null;
        } catch { return null; }
      })
    );
    const succeeded = new Set(results.filter(Boolean));
    setOrders(prev => prev.map(o =>
      succeeded.has(o.id)
        ? { ...o, tags: [...(o.tags || []).filter(t => t !== 'PO Ignored'), 'PO Ignored'] }
        : o
    ));
    if (selectedOrder && succeeded.has(selectedOrder.id)) {
      setSelectedOrder(prev => prev
        ? { ...prev, tags: [...(prev.tags || []).filter(t => t !== 'PO Ignored'), 'PO Ignored'] }
        : prev
      );
    }
    setSelectedForIgnore(new Set());
    setIgnoring(false);
  };

  const handleSelectAll = (e) => {
    e.stopPropagation();
    const allIds = visibleOrders.map(o => o.id);
    const allSelected = allIds.every(id => selectedForIgnore.has(id));
    setSelectedForIgnore(prev => {
      const next = new Set(prev);
      if (allSelected) { allIds.forEach(id => next.delete(id)); }
      else { allIds.forEach(id => next.add(id)); }
      return next;
    });
  };

  const handleRowCheckbox = (e, orderId) => {
    e.stopPropagation();
    setSelectedForIgnore(prev => {
      const next = new Set(prev);
      if (next.has(orderId)) next.delete(orderId);
      else next.add(orderId);
      return next;
    });
  };

  const goNext = () => {
    const nextIndex = pageIndex + 1;
    setPageIndex(nextIndex);
    fetchPage({ cursor: cursors[nextIndex] || null, dateFrom: appliedDateFrom, dateTo: appliedDateTo, locationId: appliedLocationId, targetIndex: nextIndex, tag: onlySkuMismatch ? 'SKU Mismatch' : '' });
  };

  const goPrev = () => {
    const prevIndex = pageIndex - 1;
    setPageIndex(prevIndex);
    fetchPage({ cursor: cursors[prevIndex] || null, dateFrom: appliedDateFrom, dateTo: appliedDateTo, locationId: appliedLocationId, targetIndex: prevIndex, tag: onlySkuMismatch ? 'SKU Mismatch' : '' });
  };

  const selectOrder = async (order) => {
    setSelectedOrder(order);
    setOrderDetail(null);
    setLines([]);
    setDetailError('');
    setCreateResult(null);
    setCreateError('');
    setSkuProgress(null);
    setAutoMatchedShop(null);
    setAutoMatchFailed(false);
    setDetailLoading(true);
    try {
      const r = await fetch(`${API_BASE}/api/b2b-po/order/${order.id}`, { headers: authHeaders() });
      if (!r.ok) throw new Error(`Server error ${r.status}`);
      const reader = r.body.getReader();
      const decoder = new TextDecoder();
      let buffer = '';
      while (true) {
        const { done, value } = await reader.read();
        if (done) break;
        buffer += decoder.decode(value, { stream: true });
        const parts = buffer.split('\n');
        buffer = parts.pop();
        for (const part of parts) {
          if (!part.startsWith('data: ')) continue;
          try {
            const ev = JSON.parse(part.slice(6));
            if (ev.error) throw new Error(ev.error);
            if (ev.resolving) setSkuProgress(ev.resolving);
            if (ev.result) {
              setOrderDetail(ev.result.order);
              setLines(ev.result.lines || []);
              setSkuProgress(null);
              setPoNote(ev.result.order?.note || '');
            }
          } catch (parseErr) {
            if (parseErr.message !== 'Unexpected end of JSON input') throw parseErr;
          }
        }
      }
    } catch (e) {
      setDetailError(e.message);
      setSkuProgress(null);
    } finally {
      setDetailLoading(false);
    }
  };

  const createPO = async () => {
    if (!selectedOrder || !selectedShop) return;
    setCreating(true);
    setCreateLog([]);
    setCreateResult(null);
    setCreateError('');
    setProgress(0);
    setProgressTotal(lines.filter(l => l.found).length);
    setModalOpen(true);
    const log = (text, tone = 'subdued') => setCreateLog(prev => [...prev, { text, tone }]);
    try {
      const r = await fetch(`${API_BASE}/api/b2b-po/create`, {
        method: 'POST',
        headers: { ...authHeaders(), 'Content-Type': 'application/json' },
        body: JSON.stringify({ order_id: selectedOrder.id, shop_id: selectedShop, additional_note: poNote.trim() }),
      });
      if (!r.ok) throw new Error((await r.text()) || `Server error ${r.status}`);
      const reader = r.body.getReader();
      const decoder = new TextDecoder();
      let buffer = '';
      while (true) {
        const { done, value } = await reader.read();
        if (done) break;
        buffer += decoder.decode(value, { stream: true });
        const parts = buffer.split('\n');
        buffer = parts.pop();
        for (const part of parts) {
          if (!part.startsWith('data: ')) continue;
          try {
            const ev = JSON.parse(part.slice(6));
            if (ev.error) throw new Error(ev.error);
            if (ev.status)    log(ev.status);
            if (ev.order_id)  log(`Purchase order #${ev.order_id} created in Lightspeed`, 'success');
            if (ev.line_item) {
              const li = ev.line_item;
              log(li.status === 'added' ? `  ✓ ${li.sku}  qty=${li.qty}` : `  ✗ ${li.sku} — ${li.error || 'error'}`, li.status === 'added' ? 'success' : 'critical');
              setProgress(ev.progress || 0);
            }
            if (ev.result) {
              setCreateResult(ev.result);
              if (ev.result.tagged && ev.result.shopify_order_id) {
                setOrders(prev => prev.map(o =>
                  o.id === ev.result.shopify_order_id
                    ? { ...o, tags: [...(o.tags || []).filter(t => t !== 'PO Created'), 'PO Created'] }
                    : o
                ));
                if (selectedOrder?.id === ev.result.shopify_order_id) {
                  setSelectedOrder(prev => prev
                    ? { ...prev, tags: [...(prev.tags || []).filter(t => t !== 'PO Created'), 'PO Created'] }
                    : prev
                  );
                }
              }
            }
          } catch (parseErr) {
            if (parseErr.message !== 'Unexpected end of JSON input') throw parseErr;
          }
        }
      }
    } catch (e) {
      setCreateError(e.message);
      log(`Error: ${e.message}`, 'critical');
    } finally {
      setCreating(false);
    }
  };

  // ── Derived ───────────────────────────────────────────────────────────────
  const missingLines       = lines.filter(l => !l.found);
  const foundLines         = lines.filter(l => l.found);
  const shopOptions        = shops.map(sh => ({ label: sh.name, value: sh.shopID }));
  const orderAlreadyTagged = selectedOrder && (selectedOrder.tags || []).includes('PO Created');
  const orderOnHold        = selectedOrder?.fulfillment_status === 'on_hold';
  const canCreate          = selectedOrder && selectedShop && !detailLoading && lines.length > 0 && foundLines.length > 0 && !orderAlreadyTagged && !orderOnHold;

  // Location is now server-side; only hidePOCreated is applied client-side
  const visibleOrders = orders.filter(order => {
    if (onlySkuMismatch) return (order.tags || []).includes('SKU Mismatch');
    if (hidePOCreated && (order.tags || []).includes('PO Created')) return false;
    if (hidePOIgnored && (order.tags || []).includes('PO Ignored')) return false;
    return true;
  });
  const allPageSelected = visibleOrders.length > 0 && visibleOrders.every(o => selectedForIgnore.has(o.id));
  const somePageSelected = visibleOrders.some(o => selectedForIgnore.has(o.id));

  const locationOptions = [
    { label: 'All locations', value: '' },
    ...companyLocations.map(loc => ({ label: loc.name, value: loc.id })),
  ];

  const dateRangeLabel = selectedDateRange.start
    ? `${fmtDateShort(selectedDateRange.start)} – ${fmtDateShort(selectedDateRange.end || selectedDateRange.start)}`
    : 'Any date';

  const lineTableRows = lines.map(li => [
    <span style={{ whiteSpace: 'nowrap', fontWeight: 600, fontSize: 13 }}>{li.sku || <span style={{ color: '#8c9196' }}>—</span>}</span>,
    <Text variant="bodySm" as="span">{li.title}{li.variant_title ? ` / ${li.variant_title}` : ''}</Text>,
    li.quantity,
    fmtPrice(li.price),
    <SkuBadge found={li.found} />,
    li.found
      ? <Text variant="bodySm" as="span" tone="subdued">{li.ls_description || li.ls_item_id}</Text>
      : <Text variant="bodySm" as="span" tone="critical">Not found in Lightspeed</Text>,
  ]);

  // Column header style — sticky
  const thStyle = (align = 'left') => ({
    position: 'sticky',
    top: 0,
    zIndex: 1,
    textAlign: align,
    padding: '8px 12px',
    fontSize: 12,
    fontWeight: 600,
    color: '#6d7175',
    backgroundColor: '#f6f6f7',
    borderBottom: '1px solid #e1e3e5',
    whiteSpace: 'nowrap',
    boxShadow: '0 1px 0 #e1e3e5',
  });

  return (
    <Page title="B2B → Lightspeed Purchase Orders" fullWidth>
      <BlockStack gap="400">

        {/* ── Filter bar ────────────────────────────────────────────────── */}
        <Card>
          <Box padding="300">
            <InlineStack gap="300" blockAlign="end" wrap>

              {/* Company location */}
              <div style={{ minWidth: 220 }}>
                <Select
                  label="Company location"
                  options={locationOptions}
                  value={filterLocationId}
                  onChange={setFilterLocationId}
                  disabled={locationsLoading}
                />
              </div>

              {/* Date range — Polaris DatePicker in Popover */}
              <BlockStack gap="100">
                <Text variant="bodySm" as="p" tone="subdued">Date range</Text>
                <Popover
                  active={datePopoverActive}
                  activator={
                    <Button onClick={() => setDatePopoverActive(p => !p)} disclosure size="slim">
                      {dateRangeLabel}
                    </Button>
                  }
                  onClose={() => setDatePopoverActive(false)}
                  preferredAlignment="left"
                >
                  <Box padding="400">
                    <BlockStack gap="300">
                      <DatePicker
                        month={datePickerMonth.month}
                        year={datePickerMonth.year}
                        onChange={setSelectedDateRange}
                        onMonthChange={(month, year) => setDatePickerMonth({ month, year })}
                        selected={selectedDateRange.start ? selectedDateRange : undefined}
                        allowRange
                      />
                      {selectedDateRange.start && (
                        <Button
                          size="slim"
                          onClick={() => setSelectedDateRange({ start: null, end: null })}
                        >
                          Clear dates
                        </Button>
                      )}
                    </BlockStack>
                  </Box>
                </Popover>
              </BlockStack>

              {/* Filter toggle buttons */}
              <div style={{ paddingTop: 20 }}>
                <InlineStack gap="200">
                  <Button
                    pressed={hidePOCreated}
                    onClick={() => setHidePOCreated(v => !v)}
                  >
                    Filter PO Created
                  </Button>
                  <Button
                    pressed={hidePOIgnored}
                    onClick={() => setHidePOIgnored(v => !v)}
                  >
                    Filter PO Ignored
                  </Button>
                  <Button
                    pressed={onlySkuMismatch}
                    onClick={() => {
                      const next = !onlySkuMismatch;
                      setOnlySkuMismatch(next);
                      setCursors([null]);
                      fetchPage({ cursor: null, dateFrom: appliedDateFrom, dateTo: appliedDateTo, locationId: appliedLocationId, targetIndex: 0, tag: next ? 'SKU Mismatch' : '' });
                    }}
                  >
                    Only Show SKU Mismatch
                  </Button>
                  {selectedForIgnore.size > 0 && (
                    <Button
                      tone="critical"
                      onClick={handleIgnore}
                      loading={ignoring}
                    >
                      Ignore for PO Creation ({selectedForIgnore.size})
                    </Button>
                  )}
                </InlineStack>
              </div>

              {/* Search / Reset */}
              <div style={{ paddingTop: 20 }}>
                <InlineStack gap="200">
                  <Button onClick={handleSearch} loading={ordersLoading} variant="primary" size="slim">
                    Search
                  </Button>
                  <Button onClick={handleReset} size="slim">
                    Reset
                  </Button>
                </InlineStack>
              </div>

            </InlineStack>
          </Box>
        </Card>

        <div style={{ display: 'flex', gap: 16, alignItems: 'flex-start' }}>

          {/* ── Left column ─────────────────────────────────────────────── */}
          <div style={{ flex: '1 1 0', minWidth: 0 }}>

            <Card>
              <Box padding="400">
                <BlockStack gap="300">
                  <InlineStack align="space-between" blockAlign="center">
                    <Text variant="headingMd" as="h2">Shopify B2B Orders — SP Corp</Text>
                    <Button
                      onClick={() => fetchPage({ cursor: cursors[pageIndex] || null, dateFrom: appliedDateFrom, dateTo: appliedDateTo, locationId: appliedLocationId, targetIndex: pageIndex, tag: onlySkuMismatch ? 'SKU Mismatch' : '' })}
                      loading={ordersLoading}
                      size="slim"
                    >
                      Refresh
                    </Button>
                  </InlineStack>

                  {ordersError && (
                    <Banner tone="critical" onDismiss={() => setOrdersError('')}>{ordersError}</Banner>
                  )}

                  {/* Table — always rendered; dims while loading to prevent jump */}
                  <BlockStack gap="200">
                    <div style={{
                      borderRadius: 6,
                      border: '1px solid #e1e3e5',
                      overflowX: 'auto',
                      overflowY: 'auto',
                      minHeight: 480,
                      maxHeight: 480,
                      opacity: ordersLoading ? 0.5 : 1,
                      transition: 'opacity 0.15s',
                      pointerEvents: ordersLoading ? 'none' : 'auto',
                    }}>
                      <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
                        <thead>
                          <tr>
                            <th style={{ ...thStyle('center'), width: 40 }}>
                              <input
                                type="checkbox"
                                checked={allPageSelected}
                                ref={el => { if (el) el.indeterminate = somePageSelected && !allPageSelected; }}
                                onChange={handleSelectAll}
                                onClick={e => e.stopPropagation()}
                                style={{ cursor: 'pointer', width: 16, height: 16 }}
                              />
                            </th>
                            <th style={thStyle('left')}>Order</th>
                            <th style={thStyle('left')}>Customer</th>
                            <th style={thStyle('left')}>Date</th>
                            <th style={thStyle('left')}>Location</th>
                            <th style={thStyle('left')}>Destination</th>
                            <th style={thStyle('right')}>Total</th>
                            <th style={thStyle('left')}>PO Status</th>
                            <th style={thStyle('left')}>Fulfillment</th>
                            <th style={thStyle('right')}>Products</th>
                            <th style={thStyle('right')}>Items</th>
                          </tr>
                        </thead>
                        <tbody>
                          {!ordersLoading && visibleOrders.length === 0 && (
                            <tr>
                              <td colSpan={11} style={{ padding: '32px 12px', textAlign: 'center', color: '#6d7175', fontSize: 13 }}>
                                No orders found.
                              </td>
                            </tr>
                          )}
                          {visibleOrders.map(order => {
                            const isSelected = selectedOrder?.id === order.id;
                            const tdStyle = {
                              padding: '9px 12px',
                              borderBottom: '1px solid #e1e3e5',
                              verticalAlign: 'top',
                              backgroundColor: isSelected ? '#f0f7ff' : 'transparent',
                            };
                            return (
                              <tr
                                key={order.id}
                                onClick={() => selectOrder(order)}
                                style={{ cursor: 'pointer', outline: isSelected ? '2px solid #3b82f6' : 'none', outlineOffset: -2, backgroundColor: selectedForIgnore.has(order.id) ? '#fff0f0' : 'transparent' }}
                                onMouseEnter={e => { if (!isSelected) Array.from(e.currentTarget.cells).forEach(c => c.style.backgroundColor = selectedForIgnore.has(order.id) ? '#fff0f0' : '#f6f6f7'); }}
                                onMouseLeave={e => { if (!isSelected) Array.from(e.currentTarget.cells).forEach(c => c.style.backgroundColor = selectedForIgnore.has(order.id) ? '#fff0f0' : 'transparent'); }}
                              >
                                <td style={{ ...tdStyle, textAlign: 'center' }} onClick={e => e.stopPropagation()}>
                                  <input
                                    type="checkbox"
                                    checked={selectedForIgnore.has(order.id)}
                                    onChange={e => handleRowCheckbox(e, order.id)}
                                    onClick={e => e.stopPropagation()}
                                    style={{ cursor: 'pointer', width: 16, height: 16 }}
                                  />
                                </td>
                                <td style={tdStyle}><span style={{ fontWeight: 600, color: '#202223' }}>{order.name}</span></td>
                                <td style={{ ...tdStyle, color: '#202223', whiteSpace: 'nowrap' }}>{order.customer_name || '—'}</td>
                                <td style={{ ...tdStyle, whiteSpace: 'nowrap', color: '#6d7175' }}>{fmtDate(order.created_at)}</td>
                                <td style={tdStyle}><div style={{ fontWeight: 500, color: '#202223' }}>{order.company_location_name || order.company || '—'}</div></td>
                                <td style={{ ...tdStyle, color: '#6d7175', whiteSpace: 'nowrap' }}>{order.destination || '—'}</td>
                                <td style={{ ...tdStyle, textAlign: 'right', whiteSpace: 'nowrap', fontWeight: 500 }}>{fmtPrice(order.total_price)}</td>
                                <td style={tdStyle}>
                                  {(order.tags || []).includes('PO Created') && <Badge tone="success">PO Created</Badge>}
                                  {(order.tags || []).includes('PO Ignored') && (
                                    <span style={{
                                      display: 'inline-flex', alignItems: 'center',
                                      backgroundColor: '#fde8e8', color: '#c0392b',
                                      borderRadius: 10, padding: '2px 8px', fontSize: 12, fontWeight: 500,
                                    }}>PO Ignored</span>
                                  )}
                                </td>
                                <td style={tdStyle}><FulfillmentBadge status={order.fulfillment_status} /></td>
                                <td style={{ ...tdStyle, textAlign: 'right', color: '#6d7175' }}>{order.product_count ?? '—'}</td>
                                <td style={{ ...tdStyle, textAlign: 'right', color: '#6d7175' }}>{order.item_count ?? '—'}</td>
                              </tr>
                            );
                          })}
                        </tbody>
                      </table>
                    </div>

                    <InlineStack align="space-between" blockAlign="center">
                      <InlineStack gap="200" blockAlign="center">
                        {ordersLoading && <Spinner size="small" />}
                        <Text variant="bodySm" tone="subdued" as="span">
                          Page {pageIndex + 1}
                          {visibleOrders.length !== orders.length && <> · {visibleOrders.length} shown (filtered)</>}
                        </Text>
                      </InlineStack>
                      <Pagination
                        hasPrevious={pageIndex > 0}
                        onPrevious={goPrev}
                        hasNext={hasNextPage}
                        onNext={goNext}
                        label={`Page ${pageIndex + 1}`}
                      />
                    </InlineStack>
                  </BlockStack>
                </BlockStack>
              </Box>
            </Card>

            {/* Line items */}
            {selectedOrder && (
              <Box paddingBlockStart="400">
                <Card>
                  <Box padding="400">
                    <BlockStack gap="300">
                      <InlineStack align="space-between" blockAlign="center">
                        <Text variant="headingMd" as="h2">Line Item Analysis: {selectedOrder.name}</Text>
                        {detailLoading && (
                          <InlineStack gap="200" blockAlign="center">
                            <Spinner size="small" />
                            {skuProgress && (
                              <Text variant="bodySm" tone="subdued" as="span">
                                {skuProgress.current}/{skuProgress.total}: {skuProgress.sku}
                              </Text>
                            )}
                          </InlineStack>
                        )}
                      </InlineStack>
                      {detailLoading && skuProgress && (
                        <ProgressBar progress={Math.round((skuProgress.current / skuProgress.total) * 100)} size="small" tone="highlight" animated />
                      )}
                      {detailError && <Banner tone="critical">{detailError}</Banner>}
                      {!detailLoading && lines.length > 0 && (
                        <>
                          <InlineStack gap="200" wrap>
                            <Badge tone="success">{foundLines.length} matched</Badge>
                            {missingLines.length > 0 && <Badge tone="critical">{missingLines.length} not found in Lightspeed</Badge>}
                          </InlineStack>
                          {missingLines.length > 0 && (
                            <Banner tone="warning">
                              <Text as="p">
                                {missingLines.length} SKU{missingLines.length > 1 ? 's' : ''} not found in Lightspeed:{' '}
                                <strong>{missingLines.map(l => l.sku || '(blank)').join(', ')}</strong>. These will be skipped.
                              </Text>
                            </Banner>
                          )}
                          <DataTable
                            columnContentTypes={['text', 'text', 'numeric', 'text', 'text', 'text']}
                            headings={['SKU', 'Product', 'Qty', 'Price', 'LS Match', 'LS Item']}
                            rows={lineTableRows}
                            verticalAlign="middle"
                          />
                        </>
                      )}
                      {!detailLoading && lines.length === 0 && !detailError && (
                        <Text tone="subdued" as="p">No line items.</Text>
                      )}
                    </BlockStack>
                  </Box>
                </Card>
              </Box>
            )}
          </div>

          {/* ── Right column ─────────────────────────────────────────────── */}
          <div style={{ flex: '0 0 385px' }}>

            <Card>
              <Box padding="400">
                <BlockStack gap="300">
                  <Text variant="headingMd" as="h2">Lightspeed Location</Text>
                  {shopsLoading && <InlineStack align="center"><Spinner size="small" /></InlineStack>}
                  {!shopsLoading && shops.length > 0 && (
                    <Select
                      label="Send PO to location"
                      options={shopOptions}
                      value={selectedShop}
                      onChange={v => { setSelectedShop(v); setAutoMatchedShop(null); setAutoMatchFailed(false); }}
                    />
                  )}
                  {autoMatchedShop && (
                    <InlineStack gap="100" blockAlign="center">
                      <Badge tone="info">Auto-matched</Badge>
                      <Text variant="bodySm" tone="subdued" as="span">from shipping address</Text>
                    </InlineStack>
                  )}
                  {autoMatchFailed && selectedOrder && (
                    <Banner tone="warning">
                      <Text as="p" variant="bodySm">
                        Could not auto-match a Lightspeed location for <strong>{selectedOrder.company_location_name || selectedOrder.company || selectedOrder.name}</strong>. Please select manually.
                      </Text>
                    </Banner>
                  )}
                  {!shopsLoading && shops.length === 0 && (
                    <Text tone="subdued" as="p">Could not load locations.</Text>
                  )}
                </BlockStack>
              </Box>
            </Card>

            {selectedOrder && (
              <Box paddingBlockStart="400">
                <Card>
                  <Box padding="400">
                    <BlockStack gap="200">
                      <Text variant="headingMd" as="h2">Selected Order</Text>
                      <Divider />
                      <InlineStack align="space-between">
                        <Text tone="subdued" as="span">Order</Text>
                        <Text fontWeight="semibold" as="span">{selectedOrder.name}</Text>
                      </InlineStack>
                      {selectedOrder.company_location_name && (
                        <InlineStack align="space-between">
                          <Text tone="subdued" as="span">Location</Text>
                          <Text as="span">{selectedOrder.company_location_name}</Text>
                        </InlineStack>
                      )}
                      {selectedOrder.customer_name && (
                        <InlineStack align="space-between">
                          <Text tone="subdued" as="span">Ship-to</Text>
                          <Text as="span">{selectedOrder.customer_name}</Text>
                        </InlineStack>
                      )}
                      {selectedOrder.destination && (
                        <InlineStack align="space-between">
                          <Text tone="subdued" as="span">Destination</Text>
                          <Text as="span">{selectedOrder.destination}</Text>
                        </InlineStack>
                      )}
                      <InlineStack align="space-between">
                        <Text tone="subdued" as="span">Date</Text>
                        <Text as="span">{fmtDate(selectedOrder.created_at)}</Text>
                      </InlineStack>
                      <InlineStack align="space-between">
                        <Text tone="subdued" as="span">Total</Text>
                        <Text fontWeight="semibold" as="span">{fmtPrice(selectedOrder.total_price)}</Text>
                      </InlineStack>
                      <InlineStack align="space-between">
                        <Text tone="subdued" as="span">Items (matched)</Text>
                        <Text as="span">{foundLines.length} / {lines.length}</Text>
                      </InlineStack>
                    </BlockStack>
                  </Box>
                </Card>
              </Box>
            )}

            {selectedOrder && orderDetail && (
              <Box paddingBlockStart="400">
                <Card>
                  <Box padding="400">
                    <BlockStack gap="300">
                      <Text variant="headingMd" as="h2">Order Metafields</Text>
                      <Divider />
                      {(() => {
                        const mf = orderDetail.metafields || {};
                        const items = [
                          { label: 'Reason for order', value: mf.reason_for_order, multiline: true },
                          { label: 'Order placed by', value: mf.order_placed_by },
                          { label: 'Lightspeed transaction #', value: mf.provide_lightspeed_transaction },
                        ];
                        return items.map(({ label, value, multiline }) => (
                          <BlockStack gap="100" key={label}>
                            <Text variant="bodySm" tone="subdued" as="span">{label}</Text>
                            {value
                              ? <Text as="span" variant="bodyMd" breakWord>
                                  {multiline
                                    ? value.split('\n').map((ln, i) => <span key={i} style={{ display: 'block' }}>{ln}</span>)
                                    : value}
                                </Text>
                              : <Text as="span" tone="subdued">—</Text>
                            }
                          </BlockStack>
                        ));
                      })()}
                    </BlockStack>
                  </Box>
                </Card>
              </Box>
            )}

            {selectedOrder && (
              <Box paddingBlockStart="400">
                <Card>
                  <Box padding="400">
                    <BlockStack gap="300">
                      <Text variant="headingMd" as="h2">Create Purchase Order</Text>
                      {orderOnHold && (
                        <Banner tone="warning">This order is On Hold. A PO cannot be created until the hold is released.</Banner>
                      )}
                      {orderAlreadyTagged && !orderOnHold && (
                        <Banner tone="warning">A Lightspeed PO has already been created for this order.</Banner>
                      )}
                      {!orderAlreadyTagged && lines.length > 0 && missingLines.length > 0 && (
                        <Text tone="caution" as="p" variant="bodySm">
                          {missingLines.length} item{missingLines.length > 1 ? 's' : ''} will be skipped (not found in Lightspeed).
                        </Text>
                      )}
                      {!orderAlreadyTagged && foundLines.length === 0 && lines.length > 0 && (
                        <Banner tone="critical">No items could be matched in Lightspeed — cannot create PO.</Banner>
                      )}
                      <TextField
                        label="Add a note to the PO"
                        value={poNote}
                        onChange={setPoNote}
                        multiline={6}
                        placeholder="Optional additional note appended below the order placed line..."
                      />
                      <Button
                        variant="primary"
                        size="large"
                        disabled={!canCreate || creating}
                        loading={creating}
                        onClick={createPO}
                      >
                        Create PO in Lightspeed
                      </Button>
                    </BlockStack>
                  </Box>
                </Card>
              </Box>
            )}
          </div>
        </div>
      </BlockStack>

      {/* ── Progress modal ─────────────────────────────────────────────── */}
      <Modal
        open={modalOpen}
        onClose={() => { if (!creating) setModalOpen(false); }}
        title={creating ? 'Creating Purchase Order…' : 'Purchase Order Created'}
        primaryAction={!creating ? { content: 'Close', onAction: () => setModalOpen(false) } : undefined}
      >
        <Modal.Section>
          <BlockStack gap="400">
            {(creating || progressTotal > 0) && (
              <ProgressBar
                progress={progressTotal > 0 ? Math.round((progress / progressTotal) * 100) : 0}
                size="large"
                tone={createResult ? 'success' : 'highlight'}
                animated
              />
            )}
            {createResult && (
              <Banner tone="success">
                <BlockStack gap="100">
                  <Text fontWeight="semibold" as="p">PO #{createResult.order_id} created for {createResult.b2b_order}</Text>
                  <Text as="p">
                    {createResult.added} line item{createResult.added !== 1 ? 's' : ''} added
                    {createResult.skipped > 0 && `, ${createResult.skipped} skipped (not in Lightspeed)`}
                    {createResult.errors > 0 && `, ${createResult.errors} errors`}
                  </Text>
                  {createResult.skipped_skus?.length > 0 && (
                    <Text tone="subdued" as="p" variant="bodySm">Skipped: {createResult.skipped_skus.join(', ')}</Text>
                  )}
                </BlockStack>
              </Banner>
            )}
            {createError && <Banner tone="critical">{createError}</Banner>}
            <div style={{
              background: '#1a1a1a', borderRadius: 8, padding: '12px 16px',
              maxHeight: 280, overflowY: 'auto', fontFamily: 'monospace', fontSize: 12, lineHeight: '1.6',
            }}>
              {createLog.length === 0 && <span style={{ color: '#6d7175' }}>Starting…</span>}
              {createLog.map((entry, i) => (
                <div key={i} style={{ color: entry.tone === 'success' ? '#4ade80' : entry.tone === 'critical' ? '#f87171' : '#e5e7eb' }}>
                  {entry.text}
                </div>
              ))}
            </div>
          </BlockStack>
        </Modal.Section>
      </Modal>
    </Page>
  );
}
